import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { serialNumbersApi, customersApi, reminderApi } from "@/lib/api";
import { loadFormDraft, saveFormDraft, clearFormDraft } from "@/hooks/useFormDraft";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/SimpleToast";
import {
  Search,
  Plus,
  Hash,
  Loader2,
  Bell,
  AlertTriangle,
  Mail,
  Send,
  BarChart3,
  CalendarDays,
} from "lucide-react";
import { SerialNumberCard } from "@/components/serial-numbers/SerialNumberCard";
import { SerialNumberStatistics } from "@/components/serial-numbers/SerialNumberStatistics";
import { SerialNumberFormDialog } from "@/components/serial-numbers/SerialNumberFormDialog";
import { SerialNumberEmailDialog } from "@/components/serial-numbers/SerialNumberEmailDialog";
import { SerialNumberExpiryCalendar } from "@/components/serial-numbers/SerialNumberExpiryCalendar";
import { MobileFab } from "@/components/MobileFab";
import { ScrollFade } from "@/components/ScrollFade";
import { TableList } from "@/components/data-table/TableList";
import { StatusBadge } from "@/components/data-table/StatusBadge";
import { EmptyState } from "@/components/ListStates";
import { Eye, Trash2, CalendarDays as CalIcon, Wrench } from "lucide-react";

const serialExpiryStatus = (s) => {
  if (!s.expiry_date) return null;
  const today = new Date().toISOString().slice(0, 10);
  const soon = new Date(Date.now() + 90 * 864e5).toISOString().slice(0, 10);
  if (s.expiry_date < today) return { status: "ueberfaellig", label: "Abgelaufen", tone: "danger" };
  if (s.expiry_date <= soon) return { status: "in_bearbeitung", label: "Läuft ab", tone: "warning" };
  return { status: "aktiv", label: "Aktiv", tone: "success" };
};

const SERIAL_DRAFT_KEY = "draft:serial:new";

const serialDraftHasContent = (d) =>
  !!d && !!(d.serial_number || d.product_name || d.category || d.customer_id || d.purchase_date || d.expiry_date || d.notes);

const emptySerialForm = () => ({
  serial_number: "",
  product_name: "",
  category: "",
  customer_id: "",
  purchase_date: "",
  expiry_date: "",
  notes: ""
});

export default function SerialNumbers() {
  const [searchParams] = useSearchParams();
  const preselectedCustomer = searchParams.get("customer");
  const toast = useToast();

  const [serialNumbers, setSerialNumbers] = useState([]);
  const [expiringSerials, setExpiringSerials] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [itemForEmail, setItemForEmail] = useState(null);
  const [emailNote, setEmailNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [serialRefresh, setSerialRefresh] = useState(0);
  const reloadSerials = () => setSerialRefresh((n) => n + 1);

  const [formData, setFormData] = useState(emptySerialForm());

  const fetchData = useCallback(async () => {
    try {
      const [serialsRes, customersRes, expiringRes, smtpRes, categoriesRes, statsRes, calendarRes] = await Promise.all([
        serialNumbersApi.getAll(searchQuery || undefined),
        customersApi.getAllLite(),
        serialNumbersApi.getExpiring(),
        reminderApi.getSmtpSettings(),
        serialNumbersApi.getCategories(),
        serialNumbersApi.getStatistics(),
        serialNumbersApi.getCalendar()
      ]);
      setSerialNumbers(Array.isArray(serialsRes.data) ? serialsRes.data : []);
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      setExpiringSerials(Array.isArray(expiringRes.data) ? expiringRes.data : []);
      setSmtpConfigured(smtpRes.data.configured);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      setStatistics(statsRes.data);
      setCalendarData(Array.isArray(calendarRes.data) ? calendarRes.data : []);
    } catch (error) {
      console.warn("Seriennummern konnten nicht geladen werden", error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const onSync = () => fetchData();
    window.addEventListener("offline-sync-complete", onSync);
    return () => window.removeEventListener("offline-sync-complete", onSync);
  }, [fetchData]);

  // Sprachsteuerung: ?new=1 öffnet den Anlegen-Dialog, ?q= prefüllt die Suche
  useEffect(() => {
    if (searchParams.get("new")) {
      setDialogOpen(true);
    }
    const q = searchParams.get("q");
    if (q) {
      setSearchQuery(q);
      setActiveTab("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenDialog = (item) => {
    if (item) {
      setEditItem(item);
      setFormData({
        serial_number: item.serial_number,
        product_name: item.product_name,
        category: item.category || "",
        customer_id: item.customer_id || "",
        purchase_date: item.purchase_date || "",
        expiry_date: item.expiry_date || "",
        notes: item.notes || ""
      });
    } else {
      setEditItem(null);
      const draft = loadFormDraft(SERIAL_DRAFT_KEY);
      if (serialDraftHasContent(draft)) {
        setFormData({
          serial_number: draft.serial_number || "",
          product_name: draft.product_name || "",
          category: draft.category || "",
          customer_id: draft.customer_id || preselectedCustomer || "",
          purchase_date: draft.purchase_date || "",
          expiry_date: draft.expiry_date || "",
          notes: draft.notes || ""
        });
        setDraftRestored(true);
      } else {
        setDraftRestored(false);
        setFormData({ ...emptySerialForm(), customer_id: preselectedCustomer || "" });
      }
    }
    setDialogOpen(true);
  };

  // Entwurf einer neuen Seriennummer lokal sichern (überlebt Verbindungsabbruch/Reload)
  useEffect(() => {
    if (!dialogOpen || editItem) return;
    const t = setTimeout(() => {
      if (serialDraftHasContent(formData)) saveFormDraft(SERIAL_DRAFT_KEY, formData);
    }, 600);
    return () => clearTimeout(t);
  }, [dialogOpen, editItem, formData]);

  const handleDiscardSerialDraft = () => {
    clearFormDraft(SERIAL_DRAFT_KEY);
    setDraftRestored(false);
    setFormData({ ...emptySerialForm(), customer_id: preselectedCustomer || "" });
    toast.info("Entwurf verworfen");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...formData,
        customer_id: formData.customer_id || null,
        category: formData.category || null,
        expiry_date: formData.expiry_date || null
      };

      if (editItem) {
        const res = await serialNumbersApi.update(editItem.id, data, editItem.updated_at);
        if (res?._queued) toast.info("Offline gespeichert – wird synchronisiert, sobald wieder Verbindung besteht");
        else toast.success("Seriennummer aktualisiert");
      } else {
        const res = await serialNumbersApi.create(data);
        if (res?._queued) toast.info("Offline gespeichert – wird synchronisiert, sobald wieder Verbindung besteht");
        else toast.success("Seriennummer erstellt");
        clearFormDraft(SERIAL_DRAFT_KEY);
        setDraftRestored(false);
      }
      setDialogOpen(false);
      fetchData();
      reloadSerials();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (item) => {
    if (!window.confirm(`Seriennummer „${item.serial_number}" wirklich löschen?`)) return;
    try {
      await serialNumbersApi.delete(item.id);
      toast.success("Seriennummer gelöscht");
      fetchData();
      reloadSerials();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleOpenEmailDialog = (serial) => {
    setItemForEmail(serial);
    setEmailNote("");
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!itemForEmail) return;
    setSendingEmail(true);
    try {
      await reminderApi.sendEmail(itemForEmail.id, emailNote);
      toast.success("Erinnerungs-E-Mail gesendet");
      setEmailDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "E-Mail konnte nicht gesendet werden");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendDailySummary = async () => {
    setSendingSummary(true);
    try {
      const response = await reminderApi.sendDailySummary();
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Zusammenfassung konnte nicht gesendet werden");
    } finally {
      setSendingSummary(false);
    }
  };

  const handleDownloadCalendar = (serial) => {
    const url = `${reminderApi.getCalendarUrl(serial.id)}`;

    fetch(url, {
      credentials: "include"
    })
      .then(res => res.blob())
      .then(blob => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `erinnerung-${serial.serial_number}.ics`;
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success("Kalender-Datei heruntergeladen");
      })
      .catch(() => toast.error("Download fehlgeschlagen"));
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="serial-numbers-page">
      <MobileFab onClick={() => handleOpenDialog(null)} label="Neue Seriennummer" testid="fab-new-serial" />
      <div className="brand-hero rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Seriennummern</h1>
          <p className="text-muted-foreground mt-1">Verwalten Sie Geräte-Seriennummern</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog(null)} data-testid="add-serial-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neue Seriennummer
            </Button>
          </DialogTrigger>
          <SerialNumberFormDialog
            editItem={editItem}
            formData={formData}
            setFormData={setFormData}
            categories={categories}
            customers={customers}
            saving={saving}
            draftRestored={draftRestored}
            onDiscardDraft={handleDiscardSerialDraft}
            onSubmit={handleSubmit}
            onCancel={() => setDialogOpen(false)}
          />
        </Dialog>
      </div>

      {/* Reminder Banner */}
      {expiringSerials.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-amber-800 dark:text-amber-200">
                {expiringSerials.length} Gerät(e) mit ablaufender Garantie/Wartung
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Kunden kontaktieren für Verlängerung oder Erneuerung
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveTab("reminders")}
                className="border-amber-300 hover:bg-amber-100 dark:border-amber-700 dark:hover:bg-amber-900/50"
              >
                Anzeigen
              </Button>
              {smtpConfigured && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSendDailySummary}
                  disabled={sendingSummary}
                  data-testid="send-summary-btn"
                >
                  {sendingSummary ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                  Zusammenfassung senden
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {!smtpConfigured && (
        <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
          <CardContent className="p-4 flex items-center gap-4">
            <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <div className="flex-1">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>E-Mail nicht konfiguriert:</strong> Bitte SMTP-Einstellungen in der .env Datei hinterlegen für E-Mail-Erinnerungen.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <ScrollFade>
        <TabsList className="w-max max-w-none overflow-x-visible">
          <TabsTrigger value="all" data-testid="all-serials-tab">
            <Hash className="w-4 h-4 mr-2" />
            Alle ({serialNumbers.length})
          </TabsTrigger>
          <TabsTrigger value="reminders" data-testid="reminders-tab">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Erinnerungen ({expiringSerials.length})
          </TabsTrigger>
          <TabsTrigger value="statistics" data-testid="statistics-tab">
            <BarChart3 className="w-4 h-4 mr-2" />
            Statistiken
          </TabsTrigger>
          <TabsTrigger value="calendar" data-testid="calendar-tab">
            <CalendarDays className="w-4 h-4 mr-2" />
            Kalender
          </TabsTrigger>
        </TabsList>
        </ScrollFade>

        <TabsContent value="all" className="mt-6 space-y-4">
          <TableList
            tableKey="serial_numbers"
            fetcher={(params) => serialNumbersApi.getPaged(params).then((r) => r.data)}
            requiredColumns={["serial_number"]}
            defaultColumns={["serial_number", "product_name", "customer_name", "category", "expiry_date", "status"]}
            defaultSort={{ by: "created_at", dir: "desc" }}
            defaultFilters={{ category: "all", status: "all" }}
            filterControls={[
              { key: "category", defaultLabel: "Kategorie", width: 160, options: [
                { value: "all", label: "Alle Kategorien" },
                ...categories.map((c) => ({ value: c, label: c })),
              ] },
              { key: "status", defaultLabel: "Status", width: 150, options: [
                { value: "all", label: "Status: alle" },
                { value: "expiring", label: "Läuft ab (90 T.)" },
                { value: "expired", label: "Abgelaufen" },
                { value: "active", label: "Aktiv" },
              ] },
            ]}
            chipLabel={(k, v) => {
              if (k === "category") return `Kategorie: ${v}`;
              if (k === "status") return `Status: ${({ expiring: "läuft ab", expired: "abgelaufen", active: "aktiv" })[v] || v}`;
              return null;
            }}
            searchPlaceholder="Seriennummer, Produkt oder Kunde…"
            columns={[
              { key: "serial_number", header: "Seriennummer", sortable: true, render: (r) => (
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Hash className="w-4 h-4" /></div>
                    <span className="font-mono font-medium truncate">{r.serial_number}</span>
                  </div>
                ) },
              { key: "product_name", header: "Produkt", sortable: true, render: (r) => r.product_name || <span className="text-muted-foreground">—</span> },
              { key: "customer_name", header: "Kunde", sortable: true, render: (r) => r.customer_name || <span className="text-muted-foreground">—</span> },
              { key: "category", header: "Kategorie", render: (r) => r.category ? <StatusBadge status={r.category} label={r.category} tone="neutral" /> : <span className="text-muted-foreground">—</span> },
              { key: "expiry_date", header: "Ablauf", sortable: true, render: (r) => r.expiry_date ? new Date(r.expiry_date).toLocaleDateString("de-DE") : <span className="text-muted-foreground">—</span> },
              { key: "status", header: "Status", render: (r) => { const st = serialExpiryStatus(r); return st ? <StatusBadge status={st.status} label={st.label} tone={st.tone} /> : <span className="text-muted-foreground">—</span>; } },
              { key: "reminder", header: "Erinnerung", render: (r) => r.reminder_enabled ? <StatusBadge status="aktiv" label="aktiv" tone="info" icon={Bell} /> : <span className="text-muted-foreground">—</span> },
            ]}
            rowActions={(r) => [
              { label: "Bearbeiten", icon: Eye, onClick: () => handleOpenDialog(r) },
              ...(smtpConfigured ? [{ label: "E-Mail", icon: Mail, onClick: () => handleOpenEmailDialog(r) }] : []),
              { label: "Kalender", icon: CalIcon, onClick: () => handleDownloadCalendar(r) },
              { label: "Löschen", icon: Trash2, danger: true, separatorBefore: true, onClick: () => confirmDelete(r) },
            ]}
            bulkActions={[
              { label: "Löschen", icon: Trash2, danger: true, onClick: async (ids) => {
                  if (!window.confirm(`${ids.length} Seriennummern wirklich löschen?`)) throw new Error("cancel");
                  await serialNumbersApi.bulkDelete(ids); toast.success(`${ids.length} gelöscht`); fetchData();
                } },
            ]}
            refreshSignal={serialRefresh}
            testid="serials-list"
            mobileCard={(r) => {
              const st = serialExpiryStatus(r);
              return (
                <div>
                  <p className="font-semibold truncate font-mono text-sm">{r.serial_number}</p>
                  <div className="mt-1 text-sm text-muted-foreground space-y-0.5">
                    {r.product_name && <p className="truncate">{r.product_name}</p>}
                    {r.customer_name && <p className="truncate">{r.customer_name}</p>}
                    {r.expiry_date && <p>Ablauf: {new Date(r.expiry_date).toLocaleDateString("de-DE")}</p>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {r.category && <StatusBadge status={r.category} label={r.category} tone="neutral" />}
                    {st && <StatusBadge status={st.status} label={st.label} tone={st.tone} />}
                    {r.reminder_enabled && <StatusBadge status="aktiv" label="Erinnerung" tone="info" icon={Bell} />}
                  </div>
                </div>
              );
            }}
            emptyState={
              <EmptyState
                icon={Hash}
                title="Keine Seriennummern gefunden"
                description="Passen Sie Suche/Filter an oder erfassen Sie eine neue Seriennummer."
                action={<Button onClick={() => handleOpenDialog(null)}><Plus className="w-4 h-4 mr-2" />Erste Seriennummer anlegen</Button>}
              />
            }
          />
        </TabsContent>

        <TabsContent value="reminders" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Kunden kontaktieren
              </CardTitle>
              <CardDescription>
                Diese Geräte haben eine ablaufende oder abgelaufene Garantie/Wartung.
                Rufen Sie die Kunden an oder senden Sie Erinnerungen.
              </CardDescription>
            </CardHeader>
          </Card>

          {expiringSerials.length > 0 ? (
            <div className="grid gap-4">
              {expiringSerials.map((serial) => (
                <SerialNumberCard
                  key={serial.id}
                  serial={serial}
                  smtpConfigured={smtpConfigured}
                  onEdit={handleOpenDialog}
                  onDelete={confirmDelete}
                  onSendEmail={handleOpenEmailDialog}
                  onDownloadCalendar={handleDownloadCalendar}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="empty-state py-16">
                <Bell className="w-16 h-16 text-muted-foreground/30" />
                <h3 className="text-lg font-medium mt-4">Keine Erinnerungen</h3>
                <p className="text-muted-foreground mt-2">
                  Aktuell gibt es keine Geräte mit ablaufender Garantie/Wartung
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="statistics" className="mt-6 space-y-6">
          <SerialNumberStatistics statistics={statistics} />
        </TabsContent>

        <TabsContent value="calendar" className="mt-6">
          <SerialNumberExpiryCalendar
            calendarMonth={calendarMonth}
            setCalendarMonth={setCalendarMonth}
            calendarData={calendarData}
          />
        </TabsContent>
      </Tabs>

      <SerialNumberEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        itemForEmail={itemForEmail}
        emailNote={emailNote}
        setEmailNote={setEmailNote}
        sendingEmail={sendingEmail}
        onSend={handleSendEmail}
      />
    </div>
  );
}
